import { useNotifications } from "@result/rui/hooks";

import { Mutation } from "#/hooks/useDataLayer";

/**
 * Helper function to show notifications on successful mutations, based on the mutation type.
 *
 * @param {*} notifications - the notifications object from useNotifications hook to show notifications
 * @param {*} t - translation function from react-i18next for translating notification messages
 * @param {*} mutation - the type of mutation performed (create, update, delete) to show the appropriate notification message.
 * @param {*} tp - translation params for the notification message
 */
function notifyOnSuccessfulMutation(notifications, t, mutation, tp) {
  switch (mutation) {
    case Mutation.delete:
      notifications.addSuccessNotification(
        t("notification.generic.successfullyDeletedData", tp),
        t("notification.title.success"),
        { type: "success" }
      );
      break;
    case Mutation.create:
    case Mutation.update:
    default:
      notifications.addSuccessNotification(
        t("notification.generic.successfullyStoredData", tp),
        t("notification.title.success"),
        { type: "success" }
      );
      break;
  }
}

/**
 * Helper function to show notifications on failed mutations, based on the mutation type.
 *
 * @param {*} notifications - the notifications object from useNotifications hook to show notifications
 * @param {*} t - translation function from react-i18next for translating notification messages
 * @param {*} mutation - the type of mutation performed (create, update, delete) to show the appropriate notification message.
 * @param {*} tp - translation params for the notification message
 */
function notifyOnFailedMutation(notifications, t, mutation, tp) {
  switch (mutation) {
    case Mutation.delete:
      notifications.addErrorNotification(
        t("notification.generic.failedToDeleteDataExt", tp),
        t("notification.title.error"),

        { type: "danger" }
      );
      break;
    case Mutation.create:
    case Mutation.update:
    default:
      notifications.addErrorNotification(
        t("notification.generic.failedToStoreDataExt", tp),
        t("notification.title.error"),
        { type: "danger" }
      );
      break;
  }
}

/**
 * Hook provides generic mutation promise handler

 * @param {string|function|object} props - Either a string representing the URL to redirect to after successful mutation, or an object with the following properties:
 * @param {string|function} params.redirectTo - A URL string or a function that returns a URL string, used for navigation after a successful mutation.
 * @returns
 */
export default function useMutationPromiseHandler(props) {
  const navigate = useNavigate();
  const notifications = useNotifications();
  const { t } = useTranslation();

  const { redirectTo: $redirectTo, replace = false } =
    typeof props === "object" ? props : { redirectTo: props };

  const redirectTo = () => {
    if (!$redirectTo) {
      // skip navigation if
      // no redirectTo is provided
      return;
    }

    let url = $redirectTo;

    if (typeof url === "function") {
      url = url();
    }

    if (
      typeof url === "object" &&
      "toString" in url &&
      typeof url.toString === "function"
    ) {
      url = url.toString();
    }

    if (typeof url === "string") {
      return navigate(url, { replace });
    }

    throw new Error(
      `Invalid redirectTo prop type (${typeof $redirectTo}) for mutation promise handler`
    );
  };

  return (promise, mutation = undefined) =>
    promise
      .then(() => {
        // @todo show success notification
        notifyOnSuccessfulMutation(notifications, t, mutation);
        redirectTo();
      })
      .catch((error) => {
        // @todo show error notification
        notifyOnFailedMutation(notifications, t, mutation, {
          details: `${error?.message} - ${error?.details}`,
        });
      });
}
